import { Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@mui/styles';

import Currencies from 'arbitrip-common/general/utils/Currencies';

import IlsIcon from '@/img/icons/IlsIcon';
import UsdIcon from '@/img/icons/UsdIcon';
import EurIcon from '@/img/icons/EurIcon';
import GbpIcon from '@/img/icons/GbpIcon';

const { hotelsCurrencies } = Currencies;

const currencyIconMap = {
    ILS: IlsIcon,
    USD: UsdIcon,
    EUR: EurIcon,
    GBP: GbpIcon,
};

const SIMPLIFIED_CURRENCY_ORDER = ['ILS', 'USD', 'EUR', 'GBP'];
const hotelsCurrencyMap = Object.fromEntries(hotelsCurrencies.map((hc) => [hc.currency, hc]));

const useStyles = makeStyles((theme) => ({
    icon: {
        marginInlineStart: 0,
    },
    menu: {
        width: 266,
        borderRadius: 12,
        padding: 16,
    },
    menuList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: 0,
    },
    listItem: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderRadius: 8,
        border: '1px solid #E5E7EB',
        height: 56,
        padding: theme.spacing(2),
    },
    listItemSelected: {
        border: '1px solid var(--general-colors-black)',
    },
    listItemText: {
        fontSize: 18,
        flexGrow: 1,
    },
    title: {
        fontWeight: 700,
    },
    titlePayIn: {
        marginTop: 24,
    },
    listItemDisabled: {
        cursor: 'default',
        pointerEvents: 'none',
        '&:hover': {
            backgroundColor: 'transparent',
        },
    },
}));

const SimplifiedDisplayCurrency = ({ anchorEl, onClose, displayCurrency, chargeCurrency, onSelectCurrency }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const open = Boolean(anchorEl);

    const currencyOptions = SIMPLIFIED_CURRENCY_ORDER
        .filter((currency) => hotelsCurrencyMap[currency])
        .map((currency) => ({
            code: currency,
            icon: currencyIconMap[currency],
            fullLabel: `${currency} - ${t(`CurrencyModal.CurrencyDescription.${currency}`)}`,
        }));

    const chargeCurrencyOption = currencyOptions.find((c) => c.code === chargeCurrency);

    return (
        <Menu
            id="currency-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={(e) => {
                e.stopPropagation();
                onClose();
            }}
            MenuListProps={{ className: classes.menuList }}
            PaperProps={{ className: classes.menu }}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
        >
            <div className={classes.title}>{t('CurrencyModal.DisplayPricesIn')}:</div>
            {currencyOptions.map((curr) => (
                <MenuItem
                    key={curr.code}
                    data-testid={`currency-option-${curr.code}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        onSelectCurrency(curr.code);
                        onClose();
                    }}
                    className={`${classes.listItem} ${curr.code === displayCurrency ? classes.listItemSelected : ''}`}
                >
                    <ListItemIcon>
                        <curr.icon className={classes.icon} />
                    </ListItemIcon>
                    <ListItemText className={classes.listItemText} primary={curr.fullLabel} />
                </MenuItem>
            ))}
            {chargeCurrencyOption && (
                <>
                    <div className={`${classes.title} ${classes.titlePayIn}`}>{t('CurrencyModal.PayIn')}:</div>
                    <MenuItem
                        disableRipple
                        className={`${classes.listItem} ${classes.listItemSelected} ${classes.listItemDisabled}`}
                    >
                        <ListItemIcon>
                            <chargeCurrencyOption.icon className={classes.icon} />
                        </ListItemIcon>
                        <ListItemText className={classes.listItemText} primary={chargeCurrencyOption.fullLabel} />
                    </MenuItem>
                </>
            )}
        </Menu>
    );
};

export default SimplifiedDisplayCurrency;
